import { AsyncPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, computed, effect, input, OnChanges, OnInit, Signal, viewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NgControl, ReactiveFormsModule } from '@angular/forms';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { Compartment, Extension } from '@codemirror/state';
import {
  EditorView, EditorViewConfig, keymap, lineNumbers, placeholder,
} from '@codemirror/view';
import { injectTnFormFieldAria } from '@truenas/ui-components';
import { material } from '@uiw/codemirror-theme-material';
import { basicSetup } from 'codemirror';
import {
  BehaviorSubject, Observable, combineLatest, filter, take, tap,
} from 'rxjs';
import { languageFunctionsMap } from 'app/constants/language-functions-map.constant';
import { CodeEditorLanguage } from 'app/enums/code-editor-language.enum';
import { SelectOptionValueType } from 'app/interfaces/option.interface';
import { IxSimpleChanges } from 'app/interfaces/simple-changes.interface';
import { registeredDirectiveConfig } from 'app/modules/forms/ix-forms/directives/registered-control.directive';
import { TranslatedString } from 'app/modules/translate/translate.helper';

@Component({
  selector: 'ix-code-editor',
  templateUrl: './ix-code-editor.component.html',
  styleUrls: ['./ix-code-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
  ],
  hostDirectives: [
    { ...registeredDirectiveConfig },
  ],
})
export class IxCodeEditorComponent implements OnChanges, OnInit, AfterViewInit, ControlValueAccessor {
  protected controlDirective = inject(NgControl);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  readonly language = input<CodeEditorLanguage>();
  readonly placeholder = input<TranslatedString>('');
  readonly defaultValue = input<string>();

  /**
   * Explicit accessible name, for the standalone use with no enclosing `tn-form-field` — the
   * read-only editors in `job-logs-row` and the websocket debug panel. Inside a field, leave it
   * unset: the field's label names the editor through `aria-labelledby`.
   */
  readonly ariaLabel = input<string>();

  /**
   * Emitted as `data-test="code-editor-<testId>"` on CodeMirror's `.cm-content`, the editable
   * element the template never sees, so the e2e suite can fill the editor itself rather than its
   * wrapper. Pass a kebab-case literal.
   */
  readonly testId = input<string>();

  /**
   * ARIA wiring from the enclosing `tn-form-field`. This control renders no label of its own, so
   * the field names what the user actually focuses — which is CodeMirror's `.cm-content`, a
   * `role="textbox"` the template never sees. {@link contentAria} pushes these onto it.
   */
  private readonly fieldAria = injectTnFormFieldAria(this.ariaLabel);

  private readonly contentAria = computed(() => {
    const attributes: Record<string, string> = {};
    const ariaLabel = this.ariaLabel();
    const labelledby = this.fieldAria.labelledby();
    const describedby = this.fieldAria.describedby();
    const testId = this.testId();

    if (testId) {
      attributes['data-test'] = `code-editor-${testId}`;
    }
    if (ariaLabel) {
      attributes['aria-label'] = ariaLabel;
    }
    if (labelledby) {
      attributes['aria-labelledby'] = labelledby;
    }
    if (describedby) {
      attributes['aria-describedby'] = describedby;
    }
    if (this.fieldAria.invalid()) {
      attributes['aria-invalid'] = 'true';
    }

    return attributes;
  });

  private ariaCompartment = new Compartment();

  afterViewInit$ = new BehaviorSubject<boolean>(false);

  editableCompartment = new Compartment();

  isDisabled = false;

  protected isDisabled$ = new BehaviorSubject<boolean>(false);
  protected editorReady$ = new BehaviorSubject<boolean>(false);

  readonly inputArea: Signal<ElementRef<HTMLElement>> = viewChild.required('inputArea', { read: ElementRef });

  editorView: EditorView;

  protected value$ = new BehaviorSubject<string>('');

  get disabledState$(): Observable<boolean> {
    return this.isDisabled$.asObservable();
  }

  constructor() {
    this.controlDirective.valueAccessor = this;

    // A compartment rather than attributes in the initial config: the field resolves its label id
    // after this component is constructed, and an invalid state arrives later still.
    effect(() => {
      const attributes = this.contentAria();
      if (!this.editorView) {
        return;
      }
      this.editorView.dispatch({
        effects: this.ariaCompartment.reconfigure(EditorView.contentAttributes.of(attributes)),
      });
    });
  }

  ngOnChanges(changes: IxSimpleChanges<this>): void {
    if (changes.language?.currentValue) {
      this.afterViewInit$.pipe(
        filter(Boolean),
        take(1),
        tap(() => {
          this.initEditor();
          this.editorReady$.next(true);
        }),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe();
    }
  }

  ngOnInit(): void {
    this.handleDisableState();
    this.handleValueUpdate();
  }

  private handleDisableState(): void {
    combineLatest([
      this.editorReady$.pipe(filter(Boolean)),
      this.isDisabled$,
      this.afterViewInit$.pipe(filter(Boolean)),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ([, isDisabled]) => {
        this.editorView.dispatch({
          effects: this.editableCompartment.reconfigure(EditorView.editable.of(!isDisabled)),
        });
      },
    });
  }

  private handleValueUpdate(): void {
    combineLatest([
      this.editorReady$.pipe(filter(Boolean)),
      this.value$,
      this.afterViewInit$.pipe(filter(Boolean)),
    ]).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: ([, value]) => {
        this.updateValue(value);
      },
    });
  }

  ngAfterViewInit(): void {
    this.afterViewInit$.next(true);
  }

  initEditor(): void {
    const updateListener = EditorView.updateListener.of((update) => {
      const updatedValue = update.state.doc.toString();

      if (!update.docChanged || updatedValue === this.controlDirective.control?.value) {
        return;
      }

      this.onChange(updatedValue);
    });

    const extensions: Extension[] = [
      basicSetup,
      updateListener,
      lineNumbers(),
      history(),
      keymap.of([...defaultKeymap as unknown[], ...historyKeymap]),
      material,
      this.editableCompartment.of(EditorView.editable.of(true)),
      this.ariaCompartment.of(EditorView.contentAttributes.of(this.contentAria())),
      placeholder(this.placeholder()),
    ];

    const language = this.language();
    if (language) {
      extensions.push(languageFunctionsMap[language]());
    }

    const config: EditorViewConfig = {
      extensions,
      doc: this.controlDirective.control?.value as string || '',
      parent: this.inputArea().nativeElement,
    };
    this.editorView = new EditorView(config);
  }

  onChange: (value: string) => void = (): void => {};
  onTouch: () => void = (): void => {};

  writeValue(val: string): void {
    const valueToUse = (val === null || val === '') && this.defaultValue()
      ? this.defaultValue()
      : val || '';
    this.value$.next(valueToUse);
  }

  updateValue(val: string): void {
    const transaction = this.editorView.state.update({
      changes: {
        from: 0,
        to: this.editorView.state.doc.length,
        insert: val,
      },
    });

    if (transaction) {
      this.editorView.dispatch(transaction);
    }
    this.cdr.markForCheck();
  }

  registerOnChange(onChange: (value: SelectOptionValueType) => void): void {
    this.onChange = onChange;
  }

  registerOnTouched(onTouched: () => void): void {
    this.onTouch = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled$.next(isDisabled);
    this.isDisabled = isDisabled;
    this.cdr.markForCheck();
  }
}
